import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams, NavLink } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import Select from "react-select";
import ExcelJS from "exceljs";
import { format, formatDistanceToNow } from "date-fns";

import { useAuthContext } from "../../../hooks/useAuthContext";
import { msmartAxios } from "../../../api/axios";
import { useToast } from "../../../context/ToastContext";

import countries from "../../../components/country";
import template from "../../../components/Bulk_Upload_Template.xlsx";

import LeadControlBar from "../components/leads/LeadControlBar";
import LeadCard from "../components/leads/LeadCard";
import LeadTable from "../components/leads/LeadTable";
import LeadDrawer from "../components/leads/LeadDrawer";
import BulkUploadWizard from "../components/leads/BulkUploadWizard";
import useTeamAccess from "../hooks/useTeamAccess";
import { LeadsBodySkeleton } from "../components/MsmartSkeletons";
import ErrorState from "../../../components/ErrorState";
import { getErrorMessage } from "../../../utils/errorMessage";


function ManagerManageLeads() {
  const { user } = useAuthContext();
  const { teamId } = useParams();
  const location = useLocation();
  const { notifyError, notifySuccess } = useToast();

  // ===== VIEW PERMISSION =====

  // ===== LIST / CURSOR PAGINATION =====
  const [dbData, setDbData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const allAbortRef = useRef(null);
  const [allProgress, setAllProgress] = useState({ running: false, count: 0 });


  const DEFAULT_LIMIT = 26;
  const [limit, setLimit] = useState(DEFAULT_LIMIT);

  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState({ 1: null });
  const [hasMore, setHasMore] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const abortControllerRef = useRef(null);

  // ===== FILTER =====
  const [filterKey, setFilterKey] = useState("phone");
  const [filterValue, setFilterValue] = useState("");
  const [debouncedFilterValue, setDebouncedFilterValue] = useState("");

  // ===== VIEW MODE =====
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("msmart_manageleads_viewMode") || "card";
  });

  // ===== DELETE =====
  const [deleteDbId, setDeleteDbId] = useState(0);

  // ===== DRAWER (EDIT / REPEAT / CREATE) =====
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  // ===== COUNTRY OPTIONS (react-select) =====
  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      value: country.dialCode,
      label: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={country.flag}
            alt={country.name}
            style={{ width: "20px", marginRight: "5px" }}
          />
          {country.isoCode}
        </div>
      ),
      name: country.name,
      isoCode: country.isoCode,
    }));
  }, []);

  const filterOption = (option, inputValue) => {
    return (
      option.data.name.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.data.isoCode.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  // ===== FORM STATE (CREATE) =====
  const [createName, setCreateName] = useState("");
  const [createCountry, setCreateCountry] = useState(""); // dialCode string
  const [createMobile, setCreateMobile] = useState("");
  const [createStatus, setCreateStatus] = useState("");
  const [createRemarks, setCreateRemarks] = useState("");
  const [createFollowUp, setCreateFollowUp] = useState("");
  const [createClosedAmount, setCreateClosedAmount] = useState("");

  // ===== FORM STATE (EDIT) =====
  const [editId, setEditId] = useState("");
  const [editName, setEditName] = useState("");
  const [editCountry, setEditCountry] = useState(null); // react-select object
  const [editMobile, setEditMobile] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [editRemarks, setEditRemarks] = useState("");
  const [editFollowUp, setEditFollowUp] = useState("");
  const [editClosedAmount, setEditClosedAmount] = useState("");
  const [editAddedDate, setEditAddedDate] = useState("");

  // ===== SALES REPEAT =====
  const [repeatSalesAmt, setRepeatSalesAmt] = useState("");
  const [repeatRemark, setRepeatRemark] = useState("");
  const [activeRepeatTab, setActiveRepeatTab] = useState("form");
  const [salesHistory, setSalesHistory] = useState([]);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editRowAmount, setEditRowAmount] = useState("");
  const [editRowRemarks, setEditRowRemarks] = useState("");
  const initialClosedAmount = useRef("");
  const [closedBaseAmount, setClosedBaseAmount] = useState(0);

  // ===== BULK UPLOAD =====
  const [bulkOpen, setBulkOpen] = useState(false); // open/close modal
  // wizard states handled inside BulkUploadWizard (component)
  // but we need a callback to refresh list after upload
  const currentData = filteredData;

  // ==========================
  // Debounce filter
  // ==========================
  useEffect(() => {
    const handler = setTimeout(() => {
      const sanitizedValue =
        filterKey === "phone" ? filterValue.replace(/\D/g, "") : filterValue;
      setDebouncedFilterValue(sanitizedValue);
    }, 450);

    return () => clearTimeout(handler);
  }, [filterValue, filterKey]);

  // Persist viewMode
  useEffect(() => {
    localStorage.setItem("msmart_manageleads_viewMode", viewMode);
  }, [viewMode]);

  // Prefill searchPhone from query string
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const phone = params.get("searchPhone");
    if (phone) {
      setFilterKey("phone");
      setFilterValue(phone);
    }
  }, [location.search]);

  // Permission check
  const access = useTeamAccess(teamId);
  const view = access.view;

  // ==========================
  // FETCH PAGE (cursor pagination)
  // ==========================
  const fetchPage = async (targetPage, overrideLimit = null) => {
    let controller = null;
    try {
      setIsLoading(true);

      const currentLimit = overrideLimit || limit;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      controller = abortControllerRef.current;

      const cursorForPage = pageCursors[targetPage];

      let url = `/api/msmart/leads/cursor/${teamId}?limit=${currentLimit}`;
      if (cursorForPage) url += `&cursor=${encodeURIComponent(cursorForPage)}`;

      if (debouncedFilterValue) {
        url += `&filterKey=${filterKey}&filterValue=${encodeURIComponent(
          debouncedFilterValue
        )}`;
      }

      const res = await msmartAxios.get(url, {
        headers: { accessToken: user.token },
        signal: abortControllerRef.current.signal,
      });

      const { rows, nextCursor } = res.data;

      setDbData(rows);

      if (nextCursor) {
        setPageCursors((prev) => ({ ...prev, [targetPage + 1]: nextCursor }));
        setHasMore(true);
      } else {
        setHasMore(false);
      }
      setPage(targetPage);
    } catch (err) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
      notifyError(getErrorMessage(err, "Error fetching data"));
    } finally {
      if (abortControllerRef.current === controller) setIsLoading(false);
    }
  };

  const fetchAllLeads = async () => {
    const controller = new AbortController();
    try {
      // stop normal page fetch if running
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = null;
  
      // cancel previous all if any
      if (allAbortRef.current) allAbortRef.current.abort();
      allAbortRef.current = controller;
  
      setIsLoading(true);
      setAllProgress({ running: true, count: 0 });
  
      const batchLimit = 200; // backend cap
      let cursor = null;
      let all = [];
  
      while (true) {
        const url = buildCursorUrl({
          teamId,
          limit: batchLimit,
          cursor,
          filterKey,
          filterValue: debouncedFilterValue || "",
        });
  
        const res = await msmartAxios.get(url, {
          headers: { accessToken: user.token },
          signal: controller.signal,
        });
  
        const rows = res.data?.rows || [];
        all = all.concat(rows);
        setAllProgress({ running: true, count: all.length });
  
        const nextCursor = res.data?.nextCursor;
  
        if (!nextCursor) break;
        cursor = nextCursor;
      }
  
      setDbData(all);
  
      // "All" mode -> pagination disable
      setPage(1);
      setPageCursors({ 1: null });
      setHasMore(false);
    } catch (err) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
      notifyError(getErrorMessage(err, "Error fetching all leads"));
    } finally {
      if (allAbortRef.current === controller) {
        setIsLoading(false);
        setAllProgress({ running: false, count: 0 });
      }
    }
  };
  
  const cancelFetchAll = () => {
    if (allAbortRef.current) allAbortRef.current.abort();
  };
  

  // Reset pagination whenever filter changes
  useEffect(() => {
    setPage(1);
    setPageCursors({ 1: null });
  
    if (limit === 0) {
      setHasMore(false);
      fetchAllLeads();
    } else {
      setHasMore(true);
      fetchPage(1);
    }
    // eslint-disable-next-line
  }, [debouncedFilterValue, filterKey, limit]);  

  // Filter local data (fallback / extra)
  useEffect(() => {
    if (filterValue) {
      const filtered = dbData.filter((item) => {
        if (filterKey === "phone") {
          const fullPhone = `${item.country}${item.phone}`;
          return fullPhone.toLowerCase().includes(filterValue.toLowerCase());
        }

        if (filterKey === "createdAt" && filterValue.includes(" to ")) {
          const [start, end] = filterValue.split(" to ");
          const itemDate = new Date(item.createdAt);
          const startDate = new Date(`${start}T00:00:00`);
          const endDate = new Date(`${end}T23:59:59`);
          return itemDate >= startDate && itemDate <= endDate;
        }

        return item[filterKey]
          ?.toString()
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      });

      setFilteredData(filtered);
    } else {
      setFilteredData(dbData);
    }
  }, [filterKey, filterValue, dbData]);

  // ==========================
  // KPI COUNTS
  // ==========================
  const kpi = useMemo(() => {
    const total = currentData.length;
    const counts = { total, followUp: 0, booking: 0, closed: 0, rejected: 0 };
    for (const l of currentData) {
      if (l.status === "Follow Up") counts.followUp++;
      else if (l.status === "Booking") counts.booking++;
      else if (l.status === "Closed") counts.closed++;
      else if (l.status === "Rejected") counts.rejected++;
    }
    return counts;
  }, [currentData]);

  // ==========================
  // Dropdown click isolation
  // ==========================
  const handleDropdownToggle = (e) => {
    e.stopPropagation();
    const allDropdowns = document.querySelectorAll(".card .dropdown-menu.show");
    const parentBtnGroup = e.currentTarget.parentNode;
    const currentDropdownMenu = parentBtnGroup.querySelector(".dropdown-menu");
    allDropdowns.forEach((dropdown) => {
      if (dropdown !== currentDropdownMenu) dropdown.classList.remove("show");
    });
  };

  // ==========================
  // Phone sanitizers
  // ==========================
  const sanitizePhone = (raw) => {
    let newValue = raw.replace(/[^0-9]/g, "");
    if (newValue.startsWith("0")) newValue = newValue.slice(1);
    return newValue;
  };

  // CREATE
  const handleCreateCountryChange = (option) => {
    setCreateCountry(option?.value || "");
  };

  const handleCreatePhoneChange = (e) => {
    setCreateMobile(sanitizePhone(e.target.value));
  };

  const handleEditPhoneChange = (e) => {
    setEditMobile(sanitizePhone(e.target.value));
  };

  // ==========================
  // OPEN DRAWER HELPERS
  // ==========================
  const openCreateDrawer = () => {
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const fetchLeadForDrawer = async (id) => {
    const res = await msmartAxios.get(`/api/msmart/lead/${id}`, {
      headers: { accessToken: user.token },
    });
    const json = res?.data?.db;
    if (!json) throw new Error("LEAD_NOT_FOUND");
  
    setEditId(json.id);
    setEditName(json.name || "");
    setEditMobile(json.phone || "");
    setEditStatus(json.status || "No Status");
    setEditRemarks(json.remark || "");
    setEditClosedAmount(json.salesAmount || "");
    setEditAddedDate(json.createdAt || "");
    setEditFollowUp(json.followUpDate ? json.followUpDate : "");
    setClosedBaseAmount(parseFloat(json.salesAmount || 0));
  
    const selected = countryOptions.find(
      (o) => String(o.value) === String(json.country)
    );
    setEditCountry(selected || null);
  
    await totalClosedBaseAmount(json.id);
  
    return json;
  };
  
  const openEditDrawer = async (id) => {
    setDrawerMode("edit");
    setDrawerOpen(true);
    setDrawerLoading(true);
  
    try {
      await fetchLeadForDrawer(id);
    } catch (err) {
      notifyError(getErrorMessage(err, "No response from server, please try again"));
    } finally {
      setDrawerLoading(false);
    }
  };
  
  const openRepeatDrawer = async (id) => {
    setDrawerMode("repeat");
    setDrawerOpen(true);
    setDrawerLoading(true);
    setActiveRepeatTab("form");
  
    try {
      await fetchLeadForDrawer(id);
      // optional: kalau repeat ada history API, load sini sekali
      // await fetchSalesHistory(id);
    } catch (err) {
      notifyError(getErrorMessage(err, "No response from server, please try again"));
    } finally {
      setDrawerLoading(false);
    }
  };
  

  // ==========================
  // CREATE SUBMIT
  // ==========================
  const createDb = async (e) => {
    e.preventDefault();

    if (createFollowUp && createStatus === "No Status") {
      notifyError(
        "A Follow Up Date is set. Please select a status other than 'No Status'."
      );
      return;
    }

    const payload = {
      team: teamId,
      name: createName,
      country: createCountry,
      phone: createMobile,
      status: createStatus,
      remarks: createRemarks,
      followUp: createFollowUp,
      salesAmount: createClosedAmount,
    };

    try {
      const response = await msmartAxios.post(`/api/msmart/lead`, payload, {
        headers: { accessToken: user.token },
      });

      if (response.data.status) {
        notifySuccess("Lead created successfully");

        // reset create form
        setCreateName("");
        setCreateCountry("");
        setCreateMobile("");
        setCreateStatus("");
        setCreateRemarks("");
        setCreateFollowUp("");
        setCreateClosedAmount("");

        setDrawerOpen(false);
        fetchPage(1);
      }
    } catch (err) {
      if (err.response?.data?.error) notifyError(err.response.data.error);
      else notifyError("A server or network error occurred. Please try again.");
    }
  };

  // ==========================
  // DELETE
  // ==========================
  const deleteDB = async (e) => {
    e.preventDefault();
    try {
      const res = await msmartAxios.delete(`/api/msmart/lead/${deleteDbId}`, {
        headers: { accessToken: user.token },
      });

      if (res.data.succ) {
        notifySuccess("Database deleted successfully");
        setDeleteDbId(0);
        fetchPage(page);
      } else {
        notifyError("Unable to delete database");
      }
    } catch (err) {
      notifyError(getErrorMessage(err, "Unable to delete database"));
    }
  };

  // ==========================
  // EDIT SUBMIT
  // ==========================
  const submitEdit = async (e) => {
    e.preventDefault();

    const hasFollowUp = !!editFollowUp;
    const followUpDate = hasFollowUp ? new Date(editFollowUp) : null;

    if (hasFollowUp && followUpDate > new Date() && editStatus === "No Status") {
      notifyError(
        "Follow Up Date detected. Please select status other than 'No Status'."
      );
      return;
    }

    const countryValue =
      typeof editCountry === "object" ? editCountry.value : editCountry;

    const payload = {
      name: editName,
      country: countryValue,
      phone: editMobile,
      status: editStatus,
      remark: editRemarks,
      followUp: editFollowUp || null,
      salesAmount: editClosedAmount,
    };

    try {
      const res = await msmartAxios.put(
        `/api/msmart/lead/${teamId}/${editId}`,
        payload,
        { headers: { accessToken: user.token } }
      );

      if (res.data?.error) {
        notifyError(res.data.error);
        return;
      }

      notifySuccess("Leads updated successfully");
      setDrawerOpen(false);
      fetchPage(page);
    } catch (err) {
      notifyError(
        getErrorMessage(err, "No response from server, please try again")
      );
    }
  };

  // ==========================
  // REPEAT SALE
  // ==========================
  const submitRepeat = async (e) => {
    e.preventDefault();

    const payload = { repeatAmount: repeatSalesAmt, remarks: repeatRemark };

    try {
      const res = await msmartAxios.post(
        `/api/msmart/repeat-sale/${teamId}/${editId}`,
        payload,
        { headers: { accessToken: user.token } }
      );

      if (res.data?.error) {
        notifyError(res.data.error);
        return;
      }

      notifySuccess(res.data?.message || "Sales submitted successfully");
      setRepeatSalesAmt("");
      setRepeatRemark("");
      fetchPage(page);

      // refresh history if tab open
      if (activeRepeatTab === "history") {
        await loadSalesHistory(editId);
      }
    } catch (err) {
      notifyError(
        getErrorMessage(err, "No response from server, please try again")
      );
    }
  };

  const loadSalesHistory = async (leadId) => {
    try {
      const res = await msmartAxios.get(`/api/msmart/sales/${leadId}`, {
        headers: { accessToken: user.token },
      });
      setSalesHistory(res.data || []);
    } catch (err) {
      setSalesHistory([]);
    }
  };

  useEffect(() => {
    if (editId && drawerMode === "repeat" && activeRepeatTab === "history") {
      loadSalesHistory(editId);
    }
    // eslint-disable-next-line
  }, [editId, activeRepeatTab, drawerMode]);

  const handleSaveRepeatHistoryRow = async (id) => {
    try {
      await msmartAxios.put(
        `/api/msmart/repeat-sales/${id}`,
        { repeatAmount: editRowAmount, remarks: editRowRemarks },
        { headers: { accessToken: user.token } }
      );

      const updated = salesHistory.map((item) =>
        item.id === id
          ? { ...item, repeatAmount: editRowAmount, remarks: editRowRemarks }
          : item
      );

      setSalesHistory(updated);
      setEditingRowId(null);
      notifySuccess("Sales updated successfully");
    } catch (err) {
      notifyError(getErrorMessage(err, "Error updating"));
    }
  };

  const totalClosedBaseAmount = async (leadId) => {
    try {
      const res = await msmartAxios.get(`/api/msmart/sales/${leadId}`, {
        headers: { accessToken: user.token },
      });

      const history = res.data || [];
      const totalRepeat = history.reduce(
        (sum, item) => sum + parseFloat(item.repeatAmount || 0),
        0
      );

      initialClosedAmount.current = totalRepeat.toFixed(2);
    } catch (err) {
      initialClosedAmount.current = "";
    }
  };

  const totalRepeatAmount = useMemo(() => {
    return salesHistory.reduce(
      (sum, item) => sum + parseFloat(item.repeatAmount || 0),
      0
    );
  }, [salesHistory]);

  const totalClosedValue = useMemo(() => {
    return (closedBaseAmount + totalRepeatAmount).toFixed(2);
  }, [closedBaseAmount, totalRepeatAmount]);

  // ==========================
  // EXPORT EXCEL
  // ==========================
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Filtered Leads");

      worksheet.columns = [
        { header: "#", key: "index", width: 5 },
        { header: "Name", key: "name", width: 25 },
        { header: "Phone No.", key: "phone", width: 20 },
        { header: "Status", key: "status", width: 15 },
        { header: "Remarks", key: "remark", width: 25 },
        { header: "Follow Up Date", key: "followUpDate", width: 20 },
        { header: "Created At", key: "createdAt", width: 20 },
        { header: "Last Update", key: "updatedAt", width: 20 },
      ];

      filteredData.forEach((lead, idx) => {
        worksheet.addRow({
          index: idx + 1,
          name: lead.name,
          phone: `${lead.country}${lead.phone}`,
          status: lead.status,
          remark: lead.remark || "-",
          followUpDate: lead.followUp
            ? new Date(lead.followUp).toLocaleDateString("en-GB")
            : "-",
          createdAt: new Date(lead.createdAt).toLocaleString("en-GB"),
          updatedAt: new Date(lead.updatedAt).toLocaleString("en-GB"),
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Export_Leads_${user.username}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notifyError(getErrorMessage(err, "Failed to export Excel file"));
    }
  };

  // ==========================
  // UI HANDLERS
  // ==========================
  const onChangeLimit = (newLimit) => {
    setLimit(newLimit);
  
    // reset pagination state
    setPage(1);
    setPageCursors({ 1: null });
  
    if (newLimit === 0) {
      // ✅ recommend bila All: paksa table view (card berat)
      setViewMode("table");
      fetchAllLeads();
      return;
    }
  
    setHasMore(true);
    fetchPage(1, newLimit);
  }; 

  const onPrev = () => {
    if (limit === 0) return;
    fetchPage(page - 1);
  };
  
  const onNext = () => {
    if (limit === 0) return;
    fetchPage(page + 1);
  };
  

  const buildCursorUrl = ({ teamId, limit, cursor, filterKey, filterValue }) => {
    let url = `/api/msmart/leads/cursor/${teamId}?limit=${limit}`;
    if (cursor) url += `&cursor=${encodeURIComponent(cursor)}`;
    if (filterValue) {
      url += `&filterKey=${filterKey}&filterValue=${encodeURIComponent(filterValue)}`;
    }
    return url;
  };
  
  

  // ==========================
  // RENDER
  // ==========================
  return (
    <div className="container py-6 ss-page">
      <div className="row justify-content-center text-start">
        <div className="col-lg-12">
        <div>
            <h1 className="header-title mb-1">M-Smart</h1>
            <div className="text-muted" style={{ fontSize: 14 }}>
            Sales Activity Management.
            </div>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-md-6">
          <div className="msmart-segment my-4">
            <NavLink
              to={`/msmart/team/summary/${teamId}`}
              className={({ isActive }) =>
                `msmart-seg-link ${isActive ? "active" : ""}`
              }
            >
              Team
            </NavLink>

            <NavLink
                 to={'#'}// <-- route personal kau
              className={({ isActive }) =>
                `msmart-seg-link ${isActive ? "active" : ""}`
              }
            >
              Personal
            </NavLink>
          </div>
        </div>
      </div>

      <div className="card text-center mt-4">
        <div className="card-header text-start">
          <ul className="nav nav-tabs card-header-tabs">
            <li className="nav-item">
              <Link className="nav-link active" to="#">
                Manage
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/followup/${teamId}`}>
                Follow Up
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/summary/${teamId}`}>
                Summary
              </Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/summary/${teamId}?view=analysis`}>Analysis</Link>
            </li>
            <li className="nav-item">
              <Link className="nav-link" to={`/msmart/manager/form/${teamId}`}>
                Form
              </Link>
            </li>
          </ul>
        </div>

        <div className="card-body">
          {access.error ? (
            <ErrorState title="Unable to verify access" message={access.error} className="py-2" />
          ) : view === null ? (
            <LeadsBodySkeleton />
          ) : view === false ? (
            <div className="row justify-content-center mt-3">
              <div className="alert alert-danger text-center" role="alert">
                You do not have permission to access this team M-Smart page
              </div>
            </div>
          ) : (
            <>
              {/* KPI STRIP */}

              {/* CONTROL BAR */}
              <LeadControlBar
                filterKey={filterKey}
                setFilterKey={setFilterKey}
                filterValue={filterValue}
                setFilterValue={setFilterValue}
                viewMode={viewMode}
                setViewMode={setViewMode}
                limit={limit}
                onChangeLimit={onChangeLimit}
                onOpenCreate={openCreateDrawer}
                onOpenBulk={() => setBulkOpen(true)}
                onExport={exportToExcel}
              />

              {allProgress.running && (
                <div className="alert alert-info py-2 d-flex justify-content-between align-items-center">
                  <div className="small">
                    Loading all leads... <strong>{allProgress.count}</strong> loaded
                  </div>
                  <button className="btn btn-sm btn-outline-secondary" onClick={cancelFetchAll}>
                    Cancel
                  </button>
                </div>
              )}

              {/* PAGINATION TOP */}
              <div className="d-flex justify-content-end my-2 align-items-center">
                <button
                  className="btn btn-sm btn-light mx-1"
                  onClick={onPrev}
                  disabled={isLoading || page === 1 || limit === 0}
                >
                  <i className="bi bi-caret-left"></i>
                </button>

                <span className="mx-2">Page {page}</span>

                <button
                  className="btn btn-sm btn-light mx-1"
                  onClick={onNext}
                  disabled={isLoading || !hasMore || limit === 0}
                >
                  <i className="bi bi-caret-right"></i>
                </button>
              </div>

              <small
                className="text-muted"
                style={{ fontSize: "0.8rem", fontStyle: "italic" }}
              >
                Showing {currentData.length} leads (Page {page})
              </small>

              {/* LIST */}
              {viewMode === "card" ? (
                <div className="row mt-3">
                  {isLoading
                    ? Array.from({ length: limit === 0 ? 26 : limit }).map((_, i) => (
                        <div className="col-lg-6 col-md-12" key={i}>
                          <div className="card mb-3 border-light shadow-sm">
                            <div className="card-header d-flex justify-content-between align-items-center">
                              <div
                                className="skeleton-box"
                                style={{ width: "40%", height: "18px" }}
                              ></div>
                              <div
                                className="skeleton-box"
                                style={{
                                  width: "60px",
                                  height: "20px",
                                  borderRadius: "20px",
                                }}
                              ></div>
                            </div>
                            <div className="card-body bg-light">
                              <div
                                className="skeleton-box mb-2"
                                style={{ width: "50%", height: "15px" }}
                              ></div>
                              <div
                                className="skeleton-box"
                                style={{ width: "80%", height: "15px" }}
                              ></div>
                            </div>
                            <div className="card-footer d-flex justify-content-between">
                              <div
                                className="skeleton-box"
                                style={{ width: "30%", height: "12px" }}
                              ></div>
                              <div
                                className="skeleton-box"
                                style={{
                                  width: "40px",
                                  height: "25px",
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))
                    : currentData.length > 0
                    ? currentData.map((lead) => (
                        <div className="col-lg-6 col-md-12" key={lead.id}>
                          <LeadCard
                            lead={lead}
                            onEdit={() => openEditDrawer(lead.id)}
                            onRepeat={() => openRepeatDrawer(lead.id)}
                            onDelete={() => setDeleteDbId(lead.id)}
                            onDropdownToggle={handleDropdownToggle}
                          />
                        </div>
                      ))
                    : (
                      <div className="col-12 text-center mt-4">
                        <p className="text-muted">No leads found.</p>
                      </div>
                    )}
                </div>
              ) : (
                <LeadTable
                  isLoading={isLoading}
                  limit={limit}
                  page={page}
                  currentData={currentData}
                  onEdit={(id) => openEditDrawer(id)}
                  onRepeat={(id) => openRepeatDrawer(id)}
                  onDelete={(id) => setDeleteDbId(id)}
                />
              )}

              {/* PAGINATION BOTTOM */}
              <div className="d-flex justify-content-end my-3 align-items-center">
                <button
                  className="btn btn-sm btn-light mx-1"
                  onClick={onPrev}
                  disabled={isLoading || page === 1 || limit === 0}
                >
                  <i className="bi bi-caret-left"></i>
                </button>

                <span className="mx-2">Page {page}</span>

                <button
                  className="btn btn-sm btn-light mx-1"
                  onClick={onNext}
                  disabled={isLoading || !hasMore || limit === 0}
                >
                  <i className="bi bi-caret-right"></i>
                </button>
              </div>

              {/* DELETE MODAL */}
              <div
                className="modal fade"
                id="deleteDB"
                tabIndex="-1"
                aria-hidden="true"
              >
                <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content">
                    <div className="modal-header">
                      <button
                        type="button"
                        className="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                        onClick={() => setDeleteDbId(0)}
                      ></button>
                    </div>
                    <div className="modal-body">
                      <h5>Are you sure you want to delete this database?</h5>
                      <br />
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary mx-1"
                        data-bs-dismiss="modal"
                        onClick={() => setDeleteDbId(0)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger mx-1"
                        onClick={deleteDB}
                        data-bs-dismiss="modal"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* DRAWER */}
              <LeadDrawer
                open={drawerOpen}
                mode={drawerMode}
                isLoading={drawerLoading}
                loadingText="Fetching data..."
                onClose={() => setDrawerOpen(false)}
                teamId={teamId}
                template={template}
                // react-select configs
                countryOptions={countryOptions}
                filterOption={filterOption}
                // create states
                createName={createName}
                setCreateName={setCreateName}
                createCountry={createCountry}
                setCreateCountry={setCreateCountry}
                createMobile={createMobile}
                setCreateMobile={setCreateMobile}
                createStatus={createStatus}
                setCreateStatus={setCreateStatus}
                createRemarks={createRemarks}
                setCreateRemarks={setCreateRemarks}
                createFollowUp={createFollowUp}
                setCreateFollowUp={setCreateFollowUp}
                createClosedAmount={createClosedAmount}
                setCreateClosedAmount={setCreateClosedAmount}
                onCreateSubmit={createDb}
                onCreateCountryChange={handleCreateCountryChange}
                onCreatePhoneChange={handleCreatePhoneChange}
                // edit states
                editId={editId}
                editName={editName}
                setEditName={setEditName}
                editCountry={editCountry}
                setEditCountry={setEditCountry}
                editMobile={editMobile}
                setEditMobile={setEditMobile}
                editStatus={editStatus}
                setEditStatus={setEditStatus}
                editRemarks={editRemarks}
                setEditRemarks={setEditRemarks}
                editFollowUp={editFollowUp}
                setEditFollowUp={setEditFollowUp}
                editClosedAmount={editClosedAmount}
                setEditClosedAmount={setEditClosedAmount}
                editAddedDate={editAddedDate}
                onEditPhoneChange={handleEditPhoneChange}
                onEditSubmit={submitEdit}
                // repeat
                repeatSalesAmt={repeatSalesAmt}
                setRepeatSalesAmt={setRepeatSalesAmt}
                repeatRemark={repeatRemark}
                setRepeatRemark={setRepeatRemark}
                activeRepeatTab={activeRepeatTab}
                setActiveRepeatTab={setActiveRepeatTab}
                onRepeatSubmit={submitRepeat}
                salesHistory={salesHistory}
                setSalesHistory={setSalesHistory}
                editingRowId={editingRowId}
                setEditingRowId={setEditingRowId}
                editRowAmount={editRowAmount}
                setEditRowAmount={setEditRowAmount}
                editRowRemarks={editRowRemarks}
                setEditRowRemarks={setEditRowRemarks}
                onSaveRepeatHistoryRow={handleSaveRepeatHistoryRow}
                initialClosedAmount={initialClosedAmount}
                totalClosedValue={totalClosedValue}
              />

              {/* BULK UPLOAD MODAL (separate component) */}
              <BulkUploadWizard
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                template={template}
                teamId={teamId}
                userToken={user.token}
                notifyError={notifyError}
                notifySuccess={notifySuccess}
                onDone={() => fetchPage(1)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManagerManageLeads;
